import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import { Editor } from '@tinymce/tinymce-react';
import { useMeetingHubContext } from 'components/meetings/MeetingPage/MeetingHubContext';
import { useMeetingStaffContext } from '../../MeetingStaffContext';
import MeetingService from 'services/MeetingService';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import ConfirmationModal from '../../ConfirmationModal';
import EditorialChangeSection from '../EditorialChangeSection';
import {
  ActivityType,
  CommentActionType,
  CommentCaseType,
  CommentType,
  ConfirmationType,
  ItemType,
} from 'store/enums/BallotReviewTypes';
import { Comment, Motion } from 'store/types/Meeting';
import { defaultSnackbarErrorProps } from 'util/layout';
import {
  commentActionTypeLabel,
  commentCaseTypeLabel,
  commentTypeLabel,
  createImagesUploadHandler,
  useSafeSelectIdValue,
} from 'util/meeting';
import { defaultTinyMCEProps } from 'util/form';

import commonStyles from 'styles/common.module.scss';

const getCommentOptions = (comments: Comment[]) =>
  comments.map((c) => (
    <MenuItem key={c.id} value={c.id}>
      {`Commenter: ${c.commenter} - Comment #: ${c.number}`}
    </MenuItem>
  ));

const idLabel = 'comment';

const CommentSection: React.FunctionComponent = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { meetingId, meetingSession } = useMeetingHubContext();
  const { logActivity, comments, currentDocument, setCurrentComment, saveComment, deleteComment } =
    useMeetingStaffContext();
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedCommentId, setSelectedCommentId] = useState('');
  const [draftCommentId, setDraftCommentId] = useState(() => `${idLabel}-${Date.now()}`);
  const [type, setType] = useState<CommentType>(CommentType.VoterComment);
  const [commenter, setCommenter] = useState('');
  const [number, setNumber] = useState('');
  const [text, setText] = useState('');
  const [action, setAction] = useState<CommentActionType | undefined>();
  const [actionCommenterNumber, setActionCommenterNumber] = useState('');
  const [actionCommentNumber, setActionCommentNumber] = useState('');
  const [commentCase, setCommentCase] = useState<CommentCaseType | undefined>();
  const [editorialChange, setEditorialChange] = useState({
    fromText: '',
    toText: '',
    justification: '',
  });

  const handleEditorialChange = useCallback((motion: Motion) => {
    setEditorialChange({
      fromText: motion.fromText || '',
      toText: motion.toText || '',
      justification: motion.justification || '',
    });
  }, []);

  const commentOptions = useMemo(() => getCommentOptions(comments), [comments]);

  const safeSelectedCommentId = useSafeSelectIdValue(selectedCommentId, comments);

  const currentComment: Comment = useMemo(() => {
    return {
      id: selectedCommentId || draftCommentId,
      documentId: currentDocument?.id || '',
      type,
      commenter,
      number,
      text,
      action,
      actionCommenterNumber,
      actionCommentNumber,
      case: commentCase,
      ...(action === CommentActionType.EditorialChange ? editorialChange : {}),
    };
  }, [
    action,
    actionCommentNumber,
    actionCommenterNumber,
    commentCase,
    commenter,
    currentDocument?.id,
    draftCommentId,
    editorialChange,
    number,
    selectedCommentId,
    text,
    type,
  ]);

  const handleCommentChange = useCallback((event) => {
    const commentId = event.target.value;

    setSelectedCommentId(commentId);
    if (!commentId) {
      setDraftCommentId(`${idLabel}-${Date.now()}`);
    }
  }, []);

  const handleSaveComment = useCallback(() => {
    saveComment?.().then((saved) => {
      if (saved) {
        enqueueSnackbar(`Comment saved`, { variant: 'success' });
        setSelectedCommentId(saved.id);
        setDraftCommentId(`${idLabel}-${Date.now()}`);
      }
    });
  }, [enqueueSnackbar, saveComment]);

  const handleDeleteComment = useCallback(() => {
    if (!selectedCommentId) {
      enqueueSnackbar(`No comment selected to delete`, { variant: 'warning' });
      return;
    }

    deleteComment?.(selectedCommentId);
  }, [deleteComment, enqueueSnackbar, selectedCommentId]);

  const handleDisplayComment = useCallback(() => {
    if (!meetingSession?.currentDocument?.id) {
      enqueueSnackbar(`Document must be displayed first`, { variant: 'warning' });
      return;
    }

    saveComment?.().then((saved) => {
      if (!saved) return;

      setSelectedCommentId(saved.id);
      setDraftCommentId(`${idLabel}-${Date.now()}`);

      MeetingService.saveMeetingSession({
        ...meetingSession,
        meetingId,
        currentReject: undefined,
        currentTechnicalIssue: undefined,
        currentComment: saved,
      })
        .then(() => {
          logActivity?.({
            type: ActivityType.Comment,
            document: meetingSession.currentDocument,
            comment: saved,
          });

          enqueueSnackbar(`Displayed comment`, { variant: 'success' });
        })
        .catch((error) => {
          enqueueSnackbar(`Failed to display comment`, defaultSnackbarErrorProps);
          console.error('Error displaying comment:', error);
        });
    });
  }, [enqueueSnackbar, logActivity, meetingId, meetingSession, saveComment]);

  const handleClearComment = useCallback(() => {
    setSelectedCommentId('');
    setDraftCommentId(`${idLabel}-${Date.now()}`);
    setType(CommentType.VoterComment);
    setCommenter('');
    setNumber('');
    setText('');
    setAction(undefined);
    setActionCommenterNumber('');
    setActionCommentNumber('');
    setCommentCase(undefined);
    setEditorialChange({ fromText: '', toText: '', justification: '' });

    MeetingService.saveMeetingSession({
      ...meetingSession,
      meetingId: meetingId,
      currentReject: undefined,
      currentTechnicalIssue: undefined,
      currentComment: undefined,
    })
      .then(() => {
        enqueueSnackbar(`Cleared comment`, { variant: 'success' });
      })
      .catch((error: string) => {
        enqueueSnackbar(`Failed to display comment`, defaultSnackbarErrorProps);
        console.error('Error displaying comment:', error);
      });
  }, [enqueueSnackbar, meetingId, meetingSession]);

  const handleSaveClick = useCallback(() => {
    if (selectedCommentId) {
      setSaveModalOpen(true);
      return;
    }
    handleSaveComment();
  }, [handleSaveComment, selectedCommentId]);

  const handleSaveModalSubmit = useCallback(() => {
    setSaveModalOpen(false);
    handleSaveComment();
  }, [handleSaveComment]);

  const handleDeleteClick = useCallback(() => {
    if (selectedCommentId) {
      setDeleteModalOpen(true);
      return;
    }
    handleDeleteComment();
  }, [handleDeleteComment, selectedCommentId]);

  const handleDeleteModalSubmit = useCallback(() => {
    setDeleteModalOpen(false);
    handleDeleteComment();
  }, [handleDeleteComment]);

  useEffect(() => {
    const comment = comments.find((d) => d.id === selectedCommentId);

    if (comment) {
      setType(comment?.type || CommentType.VoterComment);
      setCommenter(comment?.commenter || '');
      setNumber(comment?.number || '');
      setText(comment?.text || '');
      setAction(comment?.action);
      setActionCommenterNumber(comment?.actionCommenterNumber || '');
      setActionCommentNumber(comment?.actionCommentNumber || '');
      setCommentCase(comment?.case);
      setEditorialChange({
        fromText: comment?.fromText || '',
        toText: comment?.toText || '',
        justification: comment?.justification || '',
      });
    }
  }, [comments, selectedCommentId]);

  useEffect(() => {
    if (action !== CommentActionType.EditorialChange) {
      setCommentCase(undefined);
    }
    if (action !== CommentActionType.AlreadyAddressed) {
      setActionCommenterNumber('');
      setActionCommentNumber('');
    }
    if (action !== CommentActionType.EditorialChange) {
      setEditorialChange({ fromText: '', toText: '', justification: '' });
    }
  }, [action]);

  useEffect(() => {
    setCurrentComment?.(currentComment);
  }, [currentComment, setCurrentComment]);

  return (
    <>
      <GridContainer>
        <GridItem md={6}>
          <TextField label={'Load Comment'} select value={safeSelectedCommentId} onChange={handleCommentChange}>
            <MenuItem value={''}>-- New Comment --</MenuItem>
            {commentOptions}
          </TextField>
        </GridItem>
        <GridItem md={6} className={commonStyles.buttonGroup}>
          <Button variant="contained" color="primary" onClick={handleSaveClick}>
            Save
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteClick}>
            Delete
          </Button>
        </GridItem>
        <GridItem>
          <RadioGroup
            name="comment-type-radio-group"
            value={type}
            onChange={(event) => setType(event.target.value as CommentType)}
          >
            <FormControlLabel
              value={CommentType.VoterComment}
              control={<Radio />}
              label={commentTypeLabel[CommentType.VoterComment]}
            />
            <FormControlLabel
              value={CommentType.RejectHandlingComment}
              control={<Radio />}
              label={commentTypeLabel[CommentType.RejectHandlingComment]}
            />
          </RadioGroup>
        </GridItem>
        <GridItem md={6}>
          <TextField
            label={'Commenter(name/company)'}
            value={commenter}
            required
            multiline
            onChange={(event) => {
              setCommenter(event.target.value);
            }}
          ></TextField>
        </GridItem>
        <GridItem md={6}>
          <TextField
            label={'Comment Number'}
            value={number}
            required
            multiline
            onChange={(event) => {
              setNumber(event.target.value);
            }}
          ></TextField>
        </GridItem>
        <GridItem>
          <FormControl className="w-100">
            <FormLabel>Comment</FormLabel>
            <Editor
              {...defaultTinyMCEProps}
              id="comment-text-editor"
              value={text}
              onEditorChange={(newValue: string) => setText(newValue)}
              init={{
                ...defaultTinyMCEProps.init,
                images_upload_handler: createImagesUploadHandler(meetingId),
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl>
            <FormLabel>Action</FormLabel>
            <RadioGroup
              name="action-type-radio-group"
              value={action ?? ''}
              onChange={(event) => setAction(event.target.value as CommentActionType)}
            >
              <FormControlLabel
                value={CommentActionType.AlreadyAddressed}
                control={<Radio />}
                label={commentActionTypeLabel[CommentActionType.AlreadyAddressed]}
              />
              <FormControlLabel
                value={CommentActionType.NoFurtherAction}
                control={<Radio />}
                label={commentActionTypeLabel[CommentActionType.NoFurtherAction]}
              />
              <FormControlLabel
                value={CommentActionType.ReferToTaskForce}
                control={<Radio />}
                label={commentActionTypeLabel[CommentActionType.ReferToTaskForce]}
              />
              <FormControlLabel
                value={CommentActionType.NewBusiness}
                control={<Radio />}
                label={commentActionTypeLabel[CommentActionType.NewBusiness]}
              />
              <FormControlLabel
                value={CommentActionType.EditorialChange}
                control={<Radio />}
                label={commentActionTypeLabel[CommentActionType.EditorialChange]}
              />
            </RadioGroup>
          </FormControl>
        </GridItem>
        {action === CommentActionType.AlreadyAddressed && (
          <>
            <GridItem md={6}>
              <TextField
                label={'Action Commenter Number'}
                value={actionCommenterNumber}
                required
                multiline
                onChange={(event) => {
                  setActionCommenterNumber(event.target.value);
                }}
              ></TextField>
            </GridItem>
            <GridItem md={6}>
              <TextField
                label={'Action Comment Number'}
                value={actionCommentNumber}
                required
                multiline
                onChange={(event) => {
                  setActionCommentNumber(event.target.value);
                }}
              ></TextField>
            </GridItem>
          </>
        )}
        {action === CommentActionType.EditorialChange && (
          <GridItem>
            <FormControl>
              <FormLabel>Case</FormLabel>
              <RadioGroup
                name="case-type-radio-group"
                value={commentCase ?? ''}
                onChange={(event) => setCommentCase(event.target.value as CommentCaseType)}
              >
                <FormControlLabel
                  value={CommentCaseType.NoVote}
                  control={<Radio />}
                  label={commentCaseTypeLabel[CommentCaseType.NoVote]}
                />
                <FormControlLabel
                  value={CommentCaseType.Voted}
                  control={<Radio />}
                  label={commentCaseTypeLabel[CommentCaseType.Voted]}
                />
              </RadioGroup>
            </FormControl>
          </GridItem>
        )}
        <GridItem className={commonStyles.buttonGroup}>
          <Button variant="contained" color="secondary" onClick={handleDisplayComment}>
            Display Comment
          </Button>
          <Button variant="contained" color="error" onClick={handleClearComment}>
            Clear Comment
          </Button>
        </GridItem>
        {commentCase === CommentCaseType.Voted && (
          <GridItem>
            <EditorialChangeSection
              key={selectedCommentId || draftCommentId}
              initialEditorialChange={editorialChange}
              onChange={handleEditorialChange}
              validate={() => {
                if (!meetingSession?.currentComment?.id) {
                  enqueueSnackbar(`Comment must be displayed first`, { variant: 'warning' });
                  return false;
                }

                return true;
              }}
            />
          </GridItem>
        )}
      </GridContainer>
      <ConfirmationModal
        itemType={ItemType.Comment}
        confirmationType={ConfirmationType.Overwrite}
        open={saveModalOpen}
        onSubmit={handleSaveModalSubmit}
        onClose={() => setSaveModalOpen(false)}
      />
      <ConfirmationModal
        itemType={ItemType.Comment}
        confirmationType={ConfirmationType.Delete}
        open={deleteModalOpen}
        onSubmit={handleDeleteModalSubmit}
        onClose={() => setDeleteModalOpen(false)}
      />
    </>
  );
};

export default CommentSection;
